import React, { useEffect, useState } from 'react';
import TextField from 'views/common/TextField';
import { Button, Form, Input, Spin } from 'antd';
import useEventCallback from 'hooks/useEventCallback';
import { useValidate, ValidateContext } from 'views/context/ValidateContext';
import FieldRegister from 'views/common/FieldRegister';
import { validateCode, validateEmail, validateRequired } from 'utils/validate';
import { useDispatch, useSelector } from 'react-redux';
import {
  cancelLogin,
  forgotPasswordRequest,
  login,
  verifyTwoFa,
} from 'store/reducers/auth';
import { API_STATUS } from 'constants/constants';
import { useLocation } from 'react-router-dom';

const Login = ({ onChangeTab, btnRef }) => {
  const [state, setState] = useState({});
  const validate = useValidate('login');
  const [isForgot, setForgot] = useState(false);
  const { status, error, use2Fa } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { email, password, code } = state;
  const { pathname } = useLocation();

  const onClickForgot = () => {
    setForgot(true);
  };

  const onChangeEmail = useEventCallback(
    (e) => {
      setState({ ...state, email: e.target.value });
    },
    [state]
  );

  const onChangePassword = useEventCallback(
    (e) => {
      setState({ ...state, password: e.target.value });
    },
    [state]
  );

  const onChangeCode = useEventCallback(
    (e) => {
      setState({ ...state, code: e.target.value });
    },
    [state]
  );

  const onClickButton = () => {
    const error = validate.validateFields();
    if (Object.keys(error || {}).length > 0) {
      return;
    }

    if (isForgot) {
      return dispatch(forgotPasswordRequest(email));
    }

    if (use2Fa) {
      return dispatch(verifyTwoFa({ verifyCode: code }));
    }
    dispatch(
      login({
        email,
        password,
      })
    );
  };

  const onClickLink = () => {
    onChangeTab();

    if (status === API_STATUS.PROCESSING) {
      dispatch(cancelLogin());
    }
  };

  useEffect(() => {
    if (pathname.includes('register')) {
      setTimeout(() => {
        setState({});
        setForgot();
      }, 2000);
    }
  }, [pathname]);

  return (
    <ValidateContext.Provider value={validate}>
      <Form name="login" onFinish={onClickButton} className="form-container">
        <div className="form-container__header">
          <div className="title">Welcome back, DSP</div>
          <div className="desc">Welcome back! Please enter your details.</div>
        </div>

        {isForgot && status === API_STATUS.SUCCESS ? (
          <div className="form-container__message">
            We have sent instructions to reset your password to you. Please
            check your email!
          </div>
        ) : (
          <>
            {!use2Fa && !isForgot && (
              <>
                <div className="form-container__login-other" ref={btnRef}></div>
                <div className="form-container__divide"></div>
              </>
            )}
            {use2Fa && (
              <FieldRegister
                value={state.code}
                onChange={onChangeCode}
                name="code"
                validate={[validateRequired('Code'), validateCode]}
              >
                {({ onBlur, onChange, value, error }) => (
                  <TextField
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    helperText={error}
                    label="Code"
                    placeholder="Enter code"
                  />
                )}
              </FieldRegister>
            )}
            {!use2Fa && (
              <FieldRegister
                value={state.email}
                onChange={onChangeEmail}
                name="email"
                validate={[validateRequired('Email'), validateEmail]}
              >
                {({ onBlur, onChange, value, error }) => (
                  <TextField
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    helperText={error}
                    label="Email address"
                    placeholder="Enter email address"
                  />
                )}
              </FieldRegister>
            )}
            {!isForgot && !use2Fa && (
              <FieldRegister
                value={state.password}
                onChange={onChangePassword}
                name="password"
                validate={[validateRequired('Password')]}
                disabled={isForgot}
              >
                {({ onBlur, onChange, value, error }) => (
                  <TextField
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    helperText={error}
                    label="Password"
                    placeholder="Enter password"
                  >
                    <Input.Password />
                  </TextField>
                )}
              </FieldRegister>
            )}
            {!isForgot && !use2Fa && (
              <div className="form-container__forgot" onClick={onClickForgot}>
                Forgot password?
              </div>
            )}
            <div className="form-container__process">
              <div>{status === API_STATUS.PROCESSING && <Spin />}</div>
              {error && <div className="error">{error}</div>}
            </div>
            <div className="form-container__button-action">
              <Button htmlType="submit">
                {isForgot ? 'Forgot Password' : use2Fa ? 'Verify' : 'Login'}
              </Button>
            </div>
          </>
        )}

        <div className="form-container__more-info">
          <span className="text">Don't have an account?</span>
          <span className="link" onClick={onClickLink}>
            Sign up for free
          </span>
        </div>
      </Form>
    </ValidateContext.Provider>
  );
};

export default React.forwardRef((props, ref) => (
  <Login btnRef={ref} {...props} />
));
